import React, { useState } from "react";
import { ColDef, ColGroupDef } from "ag-grid-community";
import ReportPageShell from "../components/ReportPageShell";
import DetailTable from "../components/DetailTable";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { useDetailQuery } from "../shared/useDetailQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { dateTimeCol, intCol, moneyCol, textCol } from "../shared/detailColumns";

const LIMIT_OPTIONS = [500, 2000, 10000, 50000];
const DEFAULT_LIMIT = 2000;

const TOOLTIP =
  "Tab Management (RP-2166) — one row per POS TAB order (open, in-use, deleted, or closed), matching the " +
  "legacy Tab Management Report. Money columns are the raw per-order amounts; Discount is the canonical " +
  "line + whole-order discount formula. Scope by event/date/vendor/location, then export. Date = paid time " +
  "(transaction_at or transaction_time); open/unpaid tabs (blank Date) are included by their created time.";

// Documents the deliberate legacy-parity decisions + the migration gaps, so QA reads them in the export.
const CSV_LEGEND = [
  "",
  "One row per POS tab order (Order.IsTab), across every status. NOT limited to settled sales.",
  "Money columns (Subtotal/Tax/Tip/Service Fee) are the RAW per-order stored amounts, shown verbatim.",
  "Discount = canonical Sum(line discount) + Sum(whole-order discount) formula; it reconciles to the Financial reports only for settled tabs.",
  "Date = paid time (transaction_at or transaction_time); blank for an open/unpaid tab. The date filter uses that paid time, or the created time when it is blank.",
  "Status = the new order status. Legacy tab lifecycle (open_tab/in_use/deleted_tab) collapses to Cancelled; processed -> Completed.",
  "Legacy Reference ID, MX Ref ID, and Android Device ID were dropped at migration and are not available.",
];

function tabColumns(): (ColDef | ColGroupDef)[] {
  return [
    intCol("orderLegacyId", "Order ID", { pinned: "left", width: 110 }),
    dateTimeCol("transactionAtUtc", "Date", { pinned: "left", width: 150 }),
    textCol("username", "Username", { width: 130 }),
    textCol("orderStatus", "Status", { width: 120 }),
    textCol("tabName", "Tab Name", { width: 160 }),
    textCol("tabNickname", "Tab Nickname", { width: 140 }),
    textCol("cardLastFour", "Last 4 of CC", { width: 110 }),
    moneyCol("subtotal", "Subtotal"),
    moneyCol("tax", "Tax"),
    moneyCol("tip", "Tip"),
    moneyCol("surcharge", "Service Fee"),
    moneyCol("discount", "Discount"),
    moneyCol("grandTotal", "Grand Total"),
    textCol("eventName", "Event", { width: 160 }),
    textCol("vendorId", "Vendor Id", { width: 150 }),
    textCol("locationId", "Location Id", { width: 150 }),
    dateTimeCol("createdAtUtc", "Created At"),
    dateTimeCol("updatedAtUtc", "Updated At"),
    textCol("items", "Items", { width: 320 }),
  ];
}

// Tab Management detail page (RP-2166) — TabManagementList.
export default function TabManagementReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters, limit });
  const state = useDetailQuery(
    submitted
      ? toQueryParams("TabManagementList", submitted.filters, { limit: submitted.limit })
      : null
  );

  return (
    <ReportPageShell
      title="Tab Management"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
      filterVendor
      filterLocation
      actions={
        <label className="flex items-center gap-1 text-sm text-gray-600">
          Row limit
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            {LIMIT_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n.toLocaleString()}
              </option>
            ))}
          </select>
        </label>
      }
    >
      {(data) => (
        <DetailTable
          data={data}
          columnDefs={tabColumns()}
          summaryMetrics={["OrderCount"]}
          emphasisMetric="OrderCount"
          exportName="TabManagement"
          csvExtra={CSV_LEGEND}
        />
      )}
    </ReportPageShell>
  );
}
